import { useState, useMemo, useEffect, useCallback } from 'react';
import { Emoji } from '@multipack/assets';

// Fetch commit hash from health endpoint
function useCommitHash() {
  const [commitHash, setCommitHash] = useState<string | null>(null);

  useEffect(() => {
    fetch('/health/detailed')
      .then((res) => res.json())
      .then((data) => {
        if (data.version?.commitShort) {
          setCommitHash(data.version.commitShort);
        }
      })
      .catch(() => {
        // Ignore errors - commit hash is optional
      });
  }, []);

  return commitHash;
}

export function TestHarness() {
  // Get room code and autoLaunch flag from URL hash (e.g., /dev/test-harness#ABCDEF:autoLaunch)
  const hash = window.location.hash.replace('#', '');
  const [hashRoom = '', ...hashFlags] = hash.split(':');
  const urlRoom = hashRoom.length === 6 ? hashRoom.toUpperCase() : '';
  const autoLaunch = hashFlags.includes('autoLaunch');

  const [roomCode, setRoomCode] = useState(urlRoom);
  const commitHash = useCommitHash();

  // Set page title
  useEffect(() => {
    document.title = roomCode
      ? `Test Harness - ${roomCode} - MultiPack`
      : 'Test Harness - MultiPack';
  }, [roomCode]);
  const [count, setCount] = useState(4);
  const [launched, setLaunched] = useState(false);

  // Auto-launch if autoLaunch flag is in URL and room code is valid
  useEffect(() => {
    if (autoLaunch && urlRoom.length === 6 && !launched) {
      setLaunched(true);
    }
  }, [autoLaunch, urlRoom, launched]);
  // Track which players have been loaded (staggered loading to avoid WebSocket flood)
  const [loadedCount, setLoadedCount] = useState(0);
  // Track refresh keys per player to force iframe reload
  const [refreshKeys, setRefreshKeys] = useState<Record<number, number>>({});

  const players = useMemo(() => {
    if (!launched) return [];

    // Use stable names with spaces for reliable rejoin testing
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      name: `Player ${i + 1}`,
    }));
  }, [count, launched]);

  const handleRefresh = useCallback((playerId: number) => {
    setRefreshKeys((prev) => ({
      ...prev,
      [playerId]: (prev[playerId] || 0) + 1,
    }));
  }, []);

  // Stagger iframe loading to avoid "Insufficient resources" WebSocket error
  useEffect(() => {
    if (launched && loadedCount < count) {
      const timer = setTimeout(() => {
        setLoadedCount((prev) => prev + 1);
      }, 100); // 100ms delay between each iframe to allow WebSocket to fully connect
      return () => clearTimeout(timer);
    }
  }, [launched, loadedCount, count]);

  const handleLaunch = () => {
    if (roomCode.length === 6) {
      setLaunched(true);
    }
  };

  const handleReset = () => {
    setLaunched(false);
    setLoadedCount(0);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-violet-400">Multiplayer Test Harness</h1>
          {commitHash && (
            <span className="text-xs text-gray-500 font-mono bg-gray-800 px-2 py-1 rounded">
              {commitHash}
            </span>
          )}
        </div>
        {launched && (
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
          >
            Reset
          </button>
        )}
      </div>

      {!launched ? (
        /* Setup Form */
        <div className="max-w-md mx-auto bg-gray-800 rounded-xl p-6 shadow-xl">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">Room Code</label>
            <input
              type="text"
              placeholder="ABCDEF"
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase().slice(0, 6))}
              maxLength={6}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-center font-mono text-2xl tracking-widest focus:outline-none focus:border-violet-500"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Number of Players: {count}
            </label>
            <input
              type="range"
              min={1}
              max={12}
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              className="w-full accent-violet-500"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>1</span>
              <span>12</span>
            </div>
          </div>

          <button
            onClick={handleLaunch}
            disabled={roomCode.length !== 6}
            className={`
              w-full py-3 px-6 rounded-lg font-bold text-lg transition-all
              ${
                roomCode.length === 6
                  ? 'bg-violet-600 hover:bg-violet-500 cursor-pointer'
                  : 'bg-gray-600 cursor-not-allowed text-gray-400'
              }
            `}
          >
            Launch {count} Player{count !== 1 ? 's' : ''}
          </button>

          <p className="text-gray-500 text-xs mt-4 text-center">
            Each player will auto-join with a stable name (Player1, Player2, etc.)
          </p>
        </div>
      ) : (
        /* Player Grid */
        <div className="grid grid-cols-4 gap-3">
          {players.map((player) => (
            <div key={player.id} className="bg-gray-800 rounded-lg overflow-hidden shadow-lg">
              <div className="bg-gray-700 px-2 py-1 flex items-center justify-between">
                <span className="font-medium text-xs truncate">{player.name}</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleRefresh(player.id)}
                    className="text-gray-400 hover:text-white transition-colors p-0.5"
                    title="Refresh (simulate disconnect/reconnect)"
                  >
                    <Emoji emoji="🔄" decorative />
                  </button>
                  <span className="text-[10px] text-gray-400">#{player.id + 1}</span>
                </div>
              </div>
              <div className="aspect-[9/16]">
                {/* Stagger iframe loading to avoid WebSocket flood */}
                {player.id < loadedCount ? (
                  <iframe
                    key={`${player.id}-${refreshKeys[player.id] || 0}`}
                    src={`/controller/#${roomCode}:${encodeURIComponent(player.name)}:autoJoin`}
                    className="w-full h-full border-0"
                    title={player.name}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-900">
                    <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
